import { useCallback, useState } from 'react';
import type { ConfigOverride, ConfigOverrideKey } from '#dn-types/host.ts';
import { useUnknownConfigOverrides } from '#hooks/useUnknownConfigOverrides.ts';

/**
 * Tracks the saved config overrides of a host/lighthouse/relay while it's being edited.
 *
 * Only the overrides our forms can render become editable state; the rest are shown read-only
 * and handed back untouched on save (see `withUnknownOverrides`), minus any the user deleted.
 *
 * @param configOverrides The host's saved overrides, or undefined when creating a new host
 * @param isEditable Narrows what this form can edit; see `partitionConfigOverrides`
 */
export function useHostConfigOverrides(
  configOverrides: ConfigOverride[] | undefined,
  isEditable?: (key: string) => key is ConfigOverrideKey
) {
  const {
    knownOverrides: savedHostOverrides,
    unknownOverrides,
    savedUnknownOverrides,
    removedUnknownIndices,
    removeUnknownOverride,
    restoreUnknownOverride,
    hasRemovedUnknownOverrides,
  } = useUnknownConfigOverrides(configOverrides, isEditable);

  const [hostOverrides, setHostOverrides] = useState(savedHostOverrides);

  const clearHostOverride = useCallback((keyToClear: ConfigOverrideKey) => {
    setHostOverrides((overrides) => overrides.filter((o) => o.key !== keyToClear));
  }, []);

  return {
    hostOverrides,
    clearHostOverride,
    savedHostOverrides,
    unknownOverrides,
    savedUnknownOverrides,
    removedUnknownIndices,
    removeUnknownOverride,
    restoreUnknownOverride,
    hasRemovedUnknownOverrides,
  };
}
